import { SelectHelperMenuItem, Text, useLocale, VFlow } from 'bold-ui'
import { useAsyncQuerySelect } from 'components/form/field/select/useAsyncQuerySelect'
import { CustomSelectFieldProps, SelectField } from 'components/form/final-form'
import { HLabel } from 'components/HLabel'
import { Cpf } from 'components/label'
import { LotacoesCuidadoCompartilhadoSelectFieldDocument } from 'graphql/hooks.generated'
import {
  LotacoesCuidadoCompartilhadoSelectFieldQuery,
  LotacoesCuidadoCompartilhadoSelectFieldQueryVariables,
} from 'graphql/types.generated'
import { useServerTime } from 'hooks/useServerTime'
import { dateAsYyyyMmDd } from 'util/date/formatDate'
import { formatNomeProfissional } from 'view/profissional/utils/utils-profissional'

import { LotacoesCuidadoCompartilhadoSelectFieldItem } from './LotacoesCuidadoCompartilhadoSelectFieldItem'

export type LotacoesCuidadoCompartilhadoSelectModel = {
  readonly hasAgendaFechada: boolean
  readonly id: string
  readonly profissional: {
    readonly id: string
    readonly nome: string
    readonly nomeSocial?: string | null
    readonly cpf: string
    readonly cns?: string | null
  }
  readonly unidadeSaude: {
    readonly id: string
    readonly nome?: string | null
    readonly cnes: string
  }
  readonly cbo: {
    readonly id: string
    readonly nome: string
    readonly cbo2002: string
  }
  readonly equipe?: {
    readonly id: string
    readonly ine: string
    readonly nome: string
  } | null
} | null

export type LotacoesCuidadoCompartilhadoSelectFieldProps = CustomSelectFieldProps<
  LotacoesCuidadoCompartilhadoSelectModel
> & {
  unidadeSaudeId: ID
  cboId?: ID
  excludeProfissionaisIds?: ID[]
  equipeSolicitanteId?: ID
  equipeExecutanteId?: ID
  isTrocaExecutante?: boolean
  isAtendimentoOdonto?: boolean
  loadItemsOnOpen?: boolean
  showUnidadeSaude?: boolean
  date?: LocalDate
}

export function LotacoesCuidadoCompartilhadoSelectField(props: LotacoesCuidadoCompartilhadoSelectFieldProps) {
  const { getServerTimeNow } = useServerTime()

  const {
    unidadeSaudeId,
    cboId,
    excludeProfissionaisIds,
    equipeSolicitanteId,
    equipeExecutanteId,
    isTrocaExecutante = false,
    isAtendimentoOdonto = false,
    loadItemsOnOpen = true,
    showUnidadeSaude = false,
    date = dateAsYyyyMmDd(getServerTimeNow()),
    ...rest
  } = props

  const locale = useLocale()

  const { skipping, selectProps } = useAsyncQuerySelect<
    LotacoesCuidadoCompartilhadoSelectModel,
    LotacoesCuidadoCompartilhadoSelectFieldQuery,
    LotacoesCuidadoCompartilhadoSelectFieldQueryVariables
  >({
    query: LotacoesCuidadoCompartilhadoSelectFieldDocument,
    extractItems: (data) => data && data.lotacoesCuidadoCompartilhado && data.lotacoesCuidadoCompartilhado.content,
    variables: (inputQuery) => ({
      input: {
        query: inputQuery,
        estabelecimentoId: unidadeSaudeId,
        cboId,
        excludeProfissionaisIds,
        equipeSolicitanteId,
        equipeExecutanteId,
        isTrocaExecutante,
        pageParams: { unpaged: true },
      },
      date: date,
    }),
    skip: (inputString) => props.disabled || (!loadItemsOnOpen ? inputString?.trim()?.length < 2 : null),
    debounceTime: 500,
  })

  return (
    <SelectField<LotacoesCuidadoCompartilhadoSelectModel>
      itemToString={itemToString}
      renderItem={renderItem(showUnidadeSaude)}
      components={{
        Item: (itemProps) => (
          <LotacoesCuidadoCompartilhadoSelectFieldItem isAtendimentoOdonto={isAtendimentoOdonto} {...itemProps} />
        ),
        ...(!loadItemsOnOpen && {
          EmptyItem: () => (
            <SelectHelperMenuItem>
              {skipping ? 'Digite para buscar um profissional.' : locale.select.emptyItem}
            </SelectHelperMenuItem>
          ),
        }),
      }}
      {...selectProps}
      {...rest}
    />
  )
}

const itemToString = (item: LotacoesCuidadoCompartilhadoSelectModel) => item?.profissional.nome

const renderItem = (showUnidadeSaude: boolean) => (item: LotacoesCuidadoCompartilhadoSelectModel) => {
  if (!item) return null

  return (
    <VFlow vSpacing={0}>
      <Text fontWeight='bold'>
        {formatNomeProfissional(item.profissional)} - <Cpf value={item.profissional.cpf} />
      </Text>
      <HLabel title='CBO '>{`${item.cbo.nome} - ${item.cbo.cbo2002}`}</HLabel>
      <HLabel title='INE '> {(item.equipe && `${item.equipe.nome} - ${item.equipe.ine}`) || 'Sem equipe'}</HLabel>
      {showUnidadeSaude && (
        <HLabel title='Unidade de saúde '>
          {item.unidadeSaude.nome} - {item.unidadeSaude.cnes}
        </HLabel>
      )}
    </VFlow>
  )
}
